/**
 * 按钮配置 - 定义所有业务按钮的图标和文本
 *
 * 业务区分原则：
 * - create: 创建新资源（vs add: 添加已有资源）
 * - delete: 删除数据（vs remove: 移除关系）
 * - edit: 编辑表单（vs update: 更新数据）
 * - detail: 详情查看（vs view: 查看操作）
 * - withdraw: 撤回操作（vs revoke: 撤销权限/状态）
 * - redo: 重做操作（vs retry: 重试请求）
 */

import type { Component } from 'vue'
import {
  Plus,
  Delete,
  Edit,
  View,
  Connection,
  Upload,
  Download,
  DocumentCopy,
  Search,
  Refresh,
  RefreshLeft,
  Link,
  ArrowRight,
  ArrowLeft,
  DataAnalysis,
  Monitor,
  Coin,
  Wallet,
  CreditCard,
  Select,
  Check,
  Close,
  Promotion,
  RemoveFilled,
  RefreshRight,
  MoreFilled,
  Operation,
  SuccessFilled,
  CircleCloseFilled
} from '@element-plus/icons-vue'

// 业务按钮类型定义
export type BusinessButtonType =
  | 'create' | 'add'
  | 'delete' | 'remove'
  | 'edit' | 'update'
  | 'detail' | 'view' | 'bindings'
  | 'import' | 'export'
  | 'upload' | 'download' | 'copy'
  | 'search'
  | 'refresh' | 'reset'
  | 'goto' | 'navigate'
  | 'statistics' | 'realtime'
  | 'recharge' | 'topup'
  | 'consume' | 'charge'
  | 'repay' | 'refund' | 'write-off'
  | 'save'
  | 'confirm' | 'submit'
  | 'cancel' | 'close'
  | 'publish'
  | 'withdraw' | 'revoke'
  | 'redo' | 'retry'
  | 'more' | 'operation'
  | 'forward' | 'next'
  | 'backward' | 'prev'
  | 'enable' | 'disable'

// 按钮配置项接口
export interface ButtonConfig {
  icon: Component // Element Plus 图标组件
  i18nKey: string // 多语言键
  colorClass: string // 色彩类名
}

// 业务按钮配置映射
export const businessButtonConfig: Record<BusinessButtonType, ButtonConfig> = {
  // CRUD 操作 - 创建/添加
  create: {
    icon: Plus,
    i18nKey: 'common.button.create',
    colorClass: 'btn-color-primary'
  },
  add: {
    icon: Plus,
    i18nKey: 'common.button.add',
    colorClass: 'btn-color-primary'
  },

  // CRUD 操作 - 删除/移除
  delete: {
    icon: Delete,
    i18nKey: 'common.button.delete',
    colorClass: 'btn-color-danger'
  },
  remove: {
    icon: Delete,
    i18nKey: 'common.button.remove',
    colorClass: 'btn-color-danger'
  },

  // CRUD 操作 - 编辑/更新
  edit: {
    icon: Edit,
    i18nKey: 'common.button.edit',
    colorClass: 'btn-color-primary'
  },
  update: {
    icon: Edit,
    i18nKey: 'common.button.update',
    colorClass: 'btn-color-primary'
  },

  // 查看操作 - 详情/查看
  detail: {
    icon: View,
    i18nKey: 'common.button.detail',
    colorClass: 'btn-color-default'
  },
  view: {
    icon: View,
    i18nKey: 'common.button.view',
    colorClass: 'btn-color-default'
  },
  bindings: {
    icon: Connection,
    i18nKey: 'common.button.bindings',
    colorClass: 'btn-color-info-light'
  },

  // 数据操作 - 导入/导出
  import: {
    icon: Upload,
    i18nKey: 'common.button.import',
    colorClass: 'btn-color-default'
  },
  export: {
    icon: Download,
    i18nKey: 'common.button.export',
    colorClass: 'btn-color-default'
  },

  // 文件操作 - 上传/下载
  upload: {
    icon: Upload,
    i18nKey: 'common.button.upload',
    colorClass: 'btn-color-default'
  },
  download: {
    icon: Download,
    i18nKey: 'common.button.download',
    colorClass: 'btn-color-default'
  },
  copy: {
    icon: DocumentCopy,
    i18nKey: 'common.button.copy',
    colorClass: 'btn-color-default-light'
  },

  // 查询操作
  search: {
    icon: Search,
    i18nKey: 'common.button.search',
    colorClass: 'btn-color-primary'
  },
  refresh: {
    icon: Refresh,
    i18nKey: 'common.button.refresh',
    colorClass: 'btn-color-default'
  },
  reset: {
    icon: RefreshLeft,
    i18nKey: 'common.button.reset',
    colorClass: 'btn-color-default'
  },

  // 导航操作
  goto: {
    icon: Link,
    i18nKey: 'common.button.goto',
    colorClass: 'btn-color-default-light'
  },
  navigate: {
    icon: Link,
    i18nKey: 'common.button.navigate',
    colorClass: 'btn-color-default-light'
  },
  statistics: {
    icon: DataAnalysis,
    i18nKey: 'common.button.statistics',
    colorClass: 'btn-color-success-light'
  },
  realtime: {
    icon: Monitor,
    i18nKey: 'common.button.realtime',
    colorClass: 'btn-color-warning-light'
  },
  forward: {
    icon: ArrowRight,
    i18nKey: 'common.button.forward',
    colorClass: 'btn-color-default'
  },
  next: {
    icon: ArrowRight,
    i18nKey: 'common.button.next',
    colorClass: 'btn-color-primary'
  },
  backward: {
    icon: ArrowLeft,
    i18nKey: 'common.button.backward',
    colorClass: 'btn-color-default'
  },
  prev: {
    icon: ArrowLeft,
    i18nKey: 'common.button.prev',
    colorClass: 'btn-color-default'
  },

  // 财务操作 - 充值
  recharge: {
    icon: Coin,
    i18nKey: 'common.button.recharge',
    colorClass: 'btn-color-success'
  },
  topup: {
    icon: Coin,
    i18nKey: 'common.button.topup',
    colorClass: 'btn-color-success'
  },

  // 财务操作 - 消费/扣费
  consume: {
    icon: Wallet,
    i18nKey: 'common.button.consume',
    colorClass: 'btn-color-warning'
  },
  charge: {
    icon: Wallet,
    i18nKey: 'common.button.charge',
    colorClass: 'btn-color-warning'
  },

  // 财务操作 - 还款/退款/销账
  repay: {
    icon: CreditCard,
    i18nKey: 'common.button.repay',
    colorClass: 'btn-color-info'
  },
  refund: {
    icon: RefreshLeft,
    i18nKey: 'common.button.refund',
    colorClass: 'btn-color-info'
  },
  'write-off': {
    icon: Check,
    i18nKey: 'common.button.writeOff',
    colorClass: 'btn-color-success'
  },

  // 表单操作
  save: {
    icon: Select,
    i18nKey: 'common.button.save',
    colorClass: 'btn-color-primary'
  },
  confirm: {
    icon: Check,
    i18nKey: 'common.button.confirm',
    colorClass: 'btn-color-primary'
  },
  submit: {
    icon: Check,
    i18nKey: 'common.button.submit',
    colorClass: 'btn-color-primary'
  },
  cancel: {
    icon: Close,
    i18nKey: 'common.button.cancel',
    colorClass: 'btn-color-default'
  },
  close: {
    icon: Close,
    i18nKey: 'common.button.close',
    colorClass: 'btn-color-default'
  },

  // 发布操作
  publish: {
    icon: Promotion,
    i18nKey: 'common.button.publish',
    colorClass: 'btn-color-success'
  },

  // 撤回/撤销 - 业务区分：撤回操作 vs 撤销权限
  withdraw: {
    icon: RemoveFilled,
    i18nKey: 'common.button.withdraw',
    colorClass: 'btn-color-warning'
  },
  revoke: {
    icon: RemoveFilled,
    i18nKey: 'common.button.revoke',
    colorClass: 'btn-color-danger'
  },

  // 重做/重试 - 业务区分：重做操作 vs 重试请求
  redo: {
    icon: RefreshRight,
    i18nKey: 'common.button.redo',
    colorClass: 'btn-color-info-light'
  },
  retry: {
    icon: RefreshRight,
    i18nKey: 'common.button.retry',
    colorClass: 'btn-color-warning'
  },

  // 其他操作
  more: {
    icon: MoreFilled,
    i18nKey: 'common.button.more',
    colorClass: 'btn-color-default-light'
  },
  operation: {
    icon: Operation,
    i18nKey: 'common.button.operation',
    colorClass: 'btn-color-default-light'
  },

  // 状态操作
  enable: {
    icon: SuccessFilled,
    i18nKey: 'common.enable',
    colorClass: 'btn-color-success'
  },
  disable: {
    icon: CircleCloseFilled,
    i18nKey: 'common.disable',
    colorClass: 'btn-color-danger'
  }
}
